// src/pages/PricingPage.jsx

import React, { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Typography,
  Container,
  Button,
  alpha,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';

import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { PRICING_CATEGORIES } from '../components/PricingSection';
import SEO from '../components/SEO';

const PRICING_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://studio.wiyoutech.in/pricing#page",
      "name": "Web Development & Digital Services Pricing | WiYou! Tech Studios Jaipur",
      "description": "Transparent website development, web application, digital menu, and design pricing packages from WiYou! Tech Studios in Jaipur, Rajasthan.",
      "url": "https://studio.wiyoutech.in/pricing",
      "isPartOf": { "@type": "WebSite", "@id": "https://studio.wiyoutech.in/#website" }
    }
  ]
};

const BROWN_THEME_PALETTE = {
  primary: '#2B211C',
  secondary: '#75675E',
  accent: '#A06E50',
  background: '#F7F4EF',
  surface: '#FCFAF6',
  textPrimary: '#2B211C',
  textSecondary: '#75675E',
  line: '#DCD4CC',
  dark: '#211915',
};

const spring = {
  type: 'spring',
  stiffness: 260,
  damping: 24,
};

const EXTENDED_PRICING_CATEGORIES = [

  ...PRICING_CATEGORIES.map((cat) => ({

    ...cat,

    items: cat.items.map((service) => ({

      ...service,

      plans: service.plans.filter((plan) => plan.tier !== 'Custom'),

    })),

  })),

  {
    id: 'automations-dashboards',
    label: 'Automations & Dashboards',
    icon: <DashboardRoundedIcon />,
    items: [
      {
        name: 'Custom Internal Dashboard & Workflow Automations',
        subtitle: 'Connect your tools, streamline data pipelines, and automate operational tasks.',
        plans: [
          {
            tier: 'Starter',
            price: '₹14,999',
            features: [
              'Basic operational dashboard',
              'Up to 2 automation triggers',
              'Google Sheets / Airtable sync',
              '1 Month Support & Fixes',
            ],
          },
          {
            tier: 'Growth',
            price: '₹29,999',
            popular: true,
            features: [
              'Advanced analytics dashboard',
              'Unlimited webhook workflows',
              'CRM / Slack / WhatsApp triggers',
              'Role-based user permissions',
            ],
          },
          {
            tier: 'Enterprise',
            price: '₹59,999',
            features: [
              'Full custom React + Node dashboard',
              'AI-driven pipeline agents',
              'Dedicated enterprise databases',
              'Priority 24/7 SLA Support',
            ],
          },
        ],
      },
    ],
  },

];

const MotionBox = motion(Box);

const PricingPage = () => {
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const navigate = useNavigate();

  const categoryCount = EXTENDED_PRICING_CATEGORIES.length;

  const serviceCount = useMemo(
    () =>
      EXTENDED_PRICING_CATEGORIES.reduce(
        (total, category) => total + category.items.length,
        0
      ),
    []
  );

  const scrollToCategory = (index, catId) => {
    setActiveCategoryIndex(index);
    document.getElementById(catId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useEffect(() => {
    const observers = EXTENDED_PRICING_CATEGORIES.map((category, index) => {
      const element = document.getElementById(category.id);
      if (!element) return null;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveCategoryIndex(index);
        },
        {
          rootMargin: '-18% 0px -68% 0px',
          threshold: 0,
        }
      );

      observer.observe(element);
      return observer;
    });

    return () => observers.forEach((observer) => observer?.disconnect());
  }, []);

  const handleWhatsAppRedirect = (serviceName, planTier, planPrice) => {
    const phoneNumber = '919876543210';

    const message = encodeURIComponent(
      `Hi WiYou! Team,\n\nI am interested in getting started with the following plan:\n\n📦 *Service:* ${serviceName}\n⭐ *Tier:* ${planTier}\n💰 *Price:* ${planPrice}\n\nPlease guide me through the next steps!`
    );

    window.open(
      `https://wa.me/${phoneNumber}?text=${message}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        backgroundColor: 'transparent',
        color: BROWN_THEME_PALETTE.textPrimary,
        overflow: 'hidden',
      }}
    >
      <SEO
        title="Web Development & Digital Services Pricing | WiYou! Tech Studios Jaipur"
        description="Explore clear website development, custom web app, and digital menu pricing from WiYou! Tech Studios in Jaipur, Rajasthan. Tailored packages for startups and businesses."
        keywords={[
          "web development cost Jaipur",
          "website design pricing Jaipur",
          "web development packages Jaipur",
          "custom software pricing India",
          "QR menu pricing Jaipur",
          "WiYou Tech Studios pricing"
        ]}
        canonicalUrl="https://studio.wiyoutech.in/pricing"
        schema={PRICING_SCHEMA}
      />
      {/* Editorial intro */}
      <Container maxWidth="xl" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 8, md: 12 } }}>
        <Button
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon />}
          sx={{
            mb: { xs: 7, md: 10 },
            px: 0,
            color: BROWN_THEME_PALETTE.secondary,
            fontWeight: 650,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'transparent',
              color: BROWN_THEME_PALETTE.primary,
            },
          }}
        >
          Back to Home
        </Button>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.35fr .65fr' },
            gap: { xs: 6, lg: 10 },
            alignItems: 'end',
          }}
        >
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: '1px',
                  backgroundColor: BROWN_THEME_PALETTE.accent,
                }}
              />
              <Typography
                sx={{
                  fontSize: '.72rem',
                  fontWeight: 800,
                  letterSpacing: '.18em',
                  textTransform: 'uppercase',
                  color: BROWN_THEME_PALETTE.accent,
                }}
              >
                WiYou! / Services & Pricing
              </Typography>
            </Box>

            <Typography
              component="h1"
              sx={{
                maxWidth: 920,
                fontSize: { xs: '3.15rem', sm: '4.5rem', md: '6.3rem' },
                lineHeight: { xs: 0.98, md: 0.91 },
                letterSpacing: '-.065em',
                fontWeight: 700,
                color: BROWN_THEME_PALETTE.primary,
              }}
            >
              Built around
              <br />
              <Box component="span" sx={{ color: BROWN_THEME_PALETTE.accent }}>
                what you need.
              </Box>
            </Typography>
          </Box>

          <Box
            sx={{
              borderLeft: { xs: 0, lg: `1px solid ${BROWN_THEME_PALETTE.line}` },
              pl: { xs: 0, lg: 5 },
              pb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.7,
                color: BROWN_THEME_PALETTE.secondary,
                maxWidth: 430,
              }}
            >
              Explore the catalog by what you are trying to build—not by a
              generic pricing table. Choose a direction, compare the scope,
              and start a conversation when something fits.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 4,
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${BROWN_THEME_PALETTE.line}`,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '1.55rem', fontWeight: 750 }}>
                  {categoryCount}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '.72rem',
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: BROWN_THEME_PALETTE.secondary,
                  }}
                >
                  Categories
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.55rem', fontWeight: 750 }}>
                  {serviceCount}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '.72rem',
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: BROWN_THEME_PALETTE.secondary,
                  }}
                >
                  Services
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Compact category rail */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          backgroundColor: alpha(BROWN_THEME_PALETTE.background, 0.94),
          backdropFilter: 'blur(18px)',
          borderTop: `1px solid ${alpha(BROWN_THEME_PALETTE.line, 0.8)}`,
          borderBottom: `1px solid ${alpha(BROWN_THEME_PALETTE.line, 0.9)}`,
        }}
      >
        <Container maxWidth="xl">
          <List
            component="nav"
            sx={{
              p: 0,
              display: 'flex',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              gap: { xs: 0.5, md: 1 },
            }}
          >
            {EXTENDED_PRICING_CATEGORIES.map((category, index) => {
              const active = activeCategoryIndex === index;

              return (
                <ListItemButton
                  key={category.id}
                  onClick={() => scrollToCategory(index, category.id)}
                  sx={{
                    minWidth: 'fit-content',
                    px: { xs: 1.5, md: 2 },
                    py: 2,
                    borderRadius: 0,
                    position: 'relative',
                    color: active
                      ? BROWN_THEME_PALETTE.primary
                      : BROWN_THEME_PALETTE.secondary,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: 16,
                      right: 16,
                      bottom: 0,
                      height: 2,
                      backgroundColor: BROWN_THEME_PALETTE.accent,
                      transform: active ? 'scaleX(1)' : 'scaleX(0)',
                      transformOrigin: 'center',
                      transition: 'transform .25s ease',
                    },
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: BROWN_THEME_PALETTE.primary,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 'auto',
                      mr: 1,
                      color: 'inherit',
                      '& svg': { fontSize: 17 },
                    }}
                  >
                    {category.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={category.label}
                    primaryTypographyProps={{
                      fontSize: '.78rem',
                      fontWeight: active ? 750 : 600,
                      whiteSpace: 'nowrap',
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Container>
      </Box>

      {/* Catalog */}
      <Container maxWidth="xl" sx={{ py: { xs: 9, md: 14 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 14, md: 20 } }}>
          {EXTENDED_PRICING_CATEGORIES.map((category, categoryIndex) => (
            <Box
              key={category.id}
              id={category.id}
              sx={{ scrollMarginTop: 95 }}
            >
              {/* Category editorial heading */}
              <MotionBox
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={spring}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '120px 1fr auto' },
                  gap: { xs: 3, md: 5 },
                  alignItems: 'end',
                  pb: 5,
                  mb: 5,
                  borderBottom: `1px solid ${BROWN_THEME_PALETTE.line}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '.7rem',
                    fontWeight: 800,
                    letterSpacing: '.16em',
                    color: BROWN_THEME_PALETTE.accent,
                  }}
                >
                  {String(categoryIndex + 1).padStart(2, '0')}
                </Typography>

                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '2.25rem', md: '3.55rem' },
                      lineHeight: 1,
                      letterSpacing: '-.045em',
                      fontWeight: 700,
                      color: BROWN_THEME_PALETTE.primary,
                    }}
                  >
                    {category.label}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1.5,
                      color: BROWN_THEME_PALETTE.secondary,
                      fontSize: '.92rem',
                    }}
                  >
                    A focused set of services for this kind of build.
                  </Typography>
                </Box>

                <NorthEastRoundedIcon
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    fontSize: 28,
                    color: BROWN_THEME_PALETTE.accent,
                    mb: 0.5,
                  }}
                />
              </MotionBox>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {category.items.map((service, serviceIndex) => (
                  <MotionBox
                    key={service.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.12 }}
                    transition={{ ...spring, delay: serviceIndex * 0.04 }}
                  >
                    {/* Service intro */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: 'minmax(260px, .72fr) 1.28fr' },
                        gap: { xs: 4, lg: 8 },
                        mb: 4,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '.68rem',
                            fontWeight: 800,
                            letterSpacing: '.15em',
                            textTransform: 'uppercase',
                            color: BROWN_THEME_PALETTE.accent,
                            mb: 1,
                          }}
                        >
                          Service {String(serviceIndex + 1).padStart(2, '0')}
                        </Typography>

                        <Typography
                          component="h2"
                          sx={{
                            fontSize: { xs: '1.55rem', md: '2rem' },
                            lineHeight: 1.08,
                            letterSpacing: '-.035em',
                            fontWeight: 700,
                            color: BROWN_THEME_PALETTE.primary,
                            maxWidth: 460,
                          }}
                        >
                          {service.name}
                        </Typography>
                      </Box>

                      <Typography
                        sx={{
                          alignSelf: 'end',
                          color: BROWN_THEME_PALETTE.secondary,
                          fontSize: '.96rem',
                          lineHeight: 1.7,
                          maxWidth: 650,
                        }}
                      >
                        {service.subtitle}
                      </Typography>
                    </Box>

                    {/* Pricing cards — intentionally flatter and editorial */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: `repeat(${Math.min(service.plans.length, 3)}, minmax(0, 1fr))`,
                        },
                        borderTop: `1px solid ${BROWN_THEME_PALETTE.line}`,
                        borderBottom: `1px solid ${BROWN_THEME_PALETTE.line}`,
                      }}
                    >
                      {service.plans.map((plan, planIndex) => (
                        <MotionBox
                          key={plan.tier}
                          whileHover={{ y: -5 }}
                          transition={spring}
                          sx={{
                            minHeight: { xs: 'auto', md: 395 },
                            p: { xs: 3, md: 3.5 },
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            backgroundColor: plan.popular
                              ? BROWN_THEME_PALETTE.surface
                              : 'transparent',
                            borderLeft: {
                              md:
                                planIndex === 0
                                  ? 'none'
                                  : `1px solid ${BROWN_THEME_PALETTE.line}`,
                            },
                            borderTop: {
                              xs:
                                planIndex === 0
                                  ? 'none'
                                  : `1px solid ${BROWN_THEME_PALETTE.line}`,
                              md: 'none',
                            },
                            boxShadow: plan.popular
                              ? `inset 0 0 0 1px ${alpha(BROWN_THEME_PALETTE.accent, 0.18)}`
                              : 'none',
                          }}
                        >
                          {plan.popular && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                px: 1.5,
                                py: 0.7,
                                backgroundColor: BROWN_THEME_PALETTE.primary,
                                color: BROWN_THEME_PALETTE.surface,
                                fontSize: '.62rem',
                                fontWeight: 800,
                                letterSpacing: '.12em',
                                textTransform: 'uppercase',
                              }}
                            >
                              Most chosen
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography
                              sx={{
                                fontSize: '.72rem',
                                fontWeight: 800,
                                letterSpacing: '.13em',
                                textTransform: 'uppercase',
                                color: BROWN_THEME_PALETTE.secondary,
                              }}
                            >
                              {plan.tier}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: '.72rem',
                                color: BROWN_THEME_PALETTE.secondary,
                              }}
                            >
                              {String(planIndex + 1).padStart(2, '0')}
                            </Typography>
                          </Box>

                          <Typography
                            sx={{
                              mt: 2,
                              mb: 3,
                              fontSize: { xs: '2rem', md: '2.35rem' },
                              lineHeight: 1,
                              fontWeight: 750,
                              letterSpacing: '-.05em',
                              color: BROWN_THEME_PALETTE.primary,
                            }}
                          >
                            {plan.price}
                          </Typography>

                          <Divider sx={{ borderColor: BROWN_THEME_PALETTE.line, mb: 3 }} />

                          <Box
                            component="ul"
                            sx={{
                              listStyle: 'none',
                              p: 0,
                              m: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1.35,
                            }}
                          >
                            {plan.features.map((feature) => (
                              <Box
                                component="li"
                                key={feature}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '18px 1fr',
                                  gap: 1,
                                  alignItems: 'start',
                                }}
                              >
                                <CheckCircleRoundedIcon
                                  sx={{
                                    fontSize: 15,
                                    color: BROWN_THEME_PALETTE.accent,
                                    mt: 0.25,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: '.84rem',
                                    lineHeight: 1.45,
                                    color: BROWN_THEME_PALETTE.secondary,
                                  }}
                                >
                                  {feature}
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          <Button
                            fullWidth
                            onClick={() =>
                              handleWhatsAppRedirect(
                                service.name,
                                plan.tier,
                                plan.price
                              )
                            }
                            endIcon={<ArrowOutwardRoundedIcon />}
                            sx={{
                              mt: 'auto',
                              pt: 2.5,
                              pb: 0,
                              justifyContent: 'space-between',
                              borderRadius: 0,
                              borderTop: `1px solid ${BROWN_THEME_PALETTE.line}`,
                              color: BROWN_THEME_PALETTE.primary,
                              fontWeight: 750,
                              fontSize: '.84rem',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: BROWN_THEME_PALETTE.accent,
                              },
                            }}
                          >
                            Start with {plan.tier}
                          </Button>
                        </MotionBox>
                      ))}
                    </Box>
                  </MotionBox>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Custom scope CTA */}
        <MotionBox
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={spring}
          sx={{
            mt: { xs: 15, md: 20 },
            py: { xs: 7, md: 9 },
            borderTop: `1px solid ${BROWN_THEME_PALETTE.primary}`,
            borderBottom: `1px solid ${BROWN_THEME_PALETTE.primary}`,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
            gap: 5,
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '.68rem',
                fontWeight: 800,
                letterSpacing: '.15em',
                textTransform: 'uppercase',
                color: BROWN_THEME_PALETTE.accent,
                mb: 2,
              }}
            >
              <LocalOfferRoundedIcon sx={{ fontSize: 16 }} />
              Tailored solutions
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '2.25rem', md: '3.4rem' },
                lineHeight: 1,
                letterSpacing: '-.05em',
                fontWeight: 700,
                maxWidth: 760,
              }}
            >
              Have a different scope in mind?
            </Typography>

            <Typography
              sx={{
                mt: 2,
                maxWidth: 680,
                color: BROWN_THEME_PALETTE.secondary,
                lineHeight: 1.7,
              }}
            >
              Tell us what you want to build, what you already have, and what
              you want to spend. We can shape the right scope around it.
            </Typography>
          </Box>

          <Button
            onClick={() => navigate('/contact')}
            endIcon={<ArrowOutwardRoundedIcon />}
            sx={{
              minWidth: { md: 205 },
              px: 3,
              py: 1.7,
              borderRadius: 0,
              backgroundColor: BROWN_THEME_PALETTE.primary,
              color: BROWN_THEME_PALETTE.surface,
              fontWeight: 750,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: BROWN_THEME_PALETTE.accent,
              },
            }}
          >
            Discuss your project
          </Button>
        </MotionBox>

        {/* Transparency */}
        <Box sx={{ mt: { xs: 13, md: 18 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1.7fr' },
              gap: { xs: 5, md: 10 },
              alignItems: 'start',
              mb: 6,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '.68rem',
                  fontWeight: 800,
                  letterSpacing: '.15em',
                  textTransform: 'uppercase',
                  color: BROWN_THEME_PALETTE.accent,
                }}
              >
                Before you start
              </Typography>

              <Typography
                sx={{
                  mt: 1.5,
                  fontSize: { xs: '2.1rem', md: '3rem' },
                  lineHeight: 1,
                  letterSpacing: '-.045em',
                  fontWeight: 700,
                }}
              >
                Clear terms.
                <br />
                No surprises.
              </Typography>
            </Box>

            <Typography
              sx={{
                maxWidth: 650,
                color: BROWN_THEME_PALETTE.secondary,
                lineHeight: 1.75,
              }}
            >
              Everything below is here so you know what the build covers,
              which services are separate, and where ongoing costs may apply.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              borderTop: `1px solid ${BROWN_THEME_PALETTE.line}`,
            }}
          >
            {[
              {
                icon: <CheckCircleRoundedIcon />,
                title: "What's Included",
                text: 'One month of support and fixes after delivery, SSL and sensible security defaults, clean code, and SEO optimisation.',
              },
              {
                icon: <SecurityRoundedIcon />,
                title: 'Third-party charges',
                text: 'Third-party accounts remain in your name and are paid directly by you. We help set them up and hand over the keys.',
              },
              {
                icon: <StorageRoundedIcon />,
                title: 'App hosting & maintenance',
                text: 'Apps with a backend, login, APIs, or databases require their own server and those running costs are separate from the build.',
              },
              {
                icon: <CodeRoundedIcon />,
                title: 'Website hosting & maintenance',
                text: 'Website hosting is renewed yearly according to what the site actually needs rather than being silently bundled into the build.',
              },
              {
                icon: <ExtensionRoundedIcon />,
                title: 'Premium add-ons',
                text: 'Additional features can be added to any tier and are quoted against your scope. Third-party costs are passed through rather than marked up.',
              },
            ].map((item, index) => (
              <Box
                key={item.title}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderBottom: `1px solid ${BROWN_THEME_PALETTE.line}`,
                  borderRight: {
                    md:
                      index % 2 === 0
                        ? `1px solid ${BROWN_THEME_PALETTE.line}`
                        : 'none',
                  },
                  gridColumn: {
                    md: index === 4 ? 'span 2' : 'auto',
                  },
                }}
              >
                <Box sx={{ color: BROWN_THEME_PALETTE.accent, mb: 2 }}>
                  {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
                </Box>
                <Typography
                  sx={{
                    fontWeight: 750,
                    fontSize: '1rem',
                    mb: 1,
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    color: BROWN_THEME_PALETTE.secondary,
                    fontSize: '.87rem',
                    lineHeight: 1.7,
                    maxWidth: 650,
                  }}
                >
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>

      {/* Minimal footer statement */}
      <Box
        sx={{
          borderTop: `1px solid ${BROWN_THEME_PALETTE.line}`,
          py: 4,
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 3,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              sx={{
                fontWeight: 750,
                fontSize: '.85rem',
              }}
            >
              WiYou!
            </Typography>
            <Typography
              sx={{
                color: BROWN_THEME_PALETTE.secondary,
                fontSize: '.8rem',
              }}
            >
              Good work starts with the right scope.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PricingPage;
